/**
 * A tela onde se acha as poucas que interessam entre centenas.
 *
 * O painel anterior agrupava por recomendação em três blocos: "Participar"
 * (sempre vazio — zero licitações em todo o acervo), "Avaliar" (18) e
 * "Descartar" (454). Um dos três buckets carregava 95% das linhas e outro
 * nunca aparecia; a divisão não organizava nada, e achar as 18 exigia rolar
 * 454 cartões irrelevantes.
 *
 * Aqui a tela abre no que interessa, com o resto a um clique de distância —
 * nunca escondido em silêncio, sempre com a contagem à vista.
 *
 * AS PEÇAS DO DESIGN SYSTEM (20/10): o cabeçalho, a busca, o seletor da
 * ordem e as abas (com a ativa ao centro no celular) saem de `src/ds`. Ficam
 * em `Oportunidades.module.css` o que é desta tela: a ordem fixa das abas de
 * decisão, o aviso das vetadas, a ajuda da aba no toque e a grade de três
 * colunas.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, type Filtros } from "../api/cliente";
import { Facetas } from "../componentes/Facetas";
import { ListaLicitacoes } from "../componentes/ListaLicitacoes";
import { PainelDetalhe } from "../componentes/PainelDetalhe";
import { Abas, Busca, Cabecalho, Seletor } from "../ds";
import css from "./Oportunidades.module.css";

/**
 * As abas, em forma de funil: Todas -> Passar -> Avaliar -> Aprovadas.
 *
 * O painel antigo empilhava tudo numa lista só, e a proporção tornava isso
 * inútil: rolava-se centenas de cartões irrelevantes para achar dezenas. Como
 * aba, cada conjunto é coerente e a contagem fica à vista antes do clique —
 * inclusive a do vazio, que também é informação.
 *
 * ── as três primeiras cortam por FAIXA DE SCORE, e a última por PESSOA ──
 *
 * `faixa` é onde o NÚMERO caiu — exceto GO e AVALIAR do edital, que vão
 * para `avaliar` com qualquer score (regra da gestão, 2026-09-16):
 * `avaliar` de `score.limiar` (70) para cima,
 * `passar` de `score.piso_passar` (50) até 69, `descartar` abaixo disso. Os
 * dois limiares moram em `config` e mudam pela tela.
 *
 * `Aprovadas` não é faixa nenhuma: é `decisao = aprovada`, a decisão de uma
 * pessoa. É o fim do funil, e o que destrava a leitura paga do edital — desde
 * a migração 0044, `POST .../analise` recusa com 422 sem aprovação prévia.
 *
 * ── faixa NÃO É recomendação, e a tela mostra as duas ──────────────────
 *
 * `recomendacao` (participar/avaliar/descartar) continua sendo a hierarquia
 * edital > parecer > score, e segue intocada — ela aparece na cor da nota de
 * cada cartão. Medido no banco: 18 licitações com score >= 70 têm NO-GO do
 * edital. A aba "Avaliar" vai mostrá-las, porque o número delas é alto; a
 * recomendação continua dizendo para descartar, porque o sistema LEU o
 * documento. Quem abrir uma dessas vê as duas coisas, que é o ponto.
 */
/**
 * AS DIMENSOES QUE AS ABAS MANDAM, zeradas a cada clique.
 *
 * Clicar numa aba tem de apagar o recorte das outras, e nao so aplicar o seu.
 * Enquanto zerava apenas `faixa` e `decisao`, o `recomendacao` das abas
 * ANTIGAS — que cortavam por participar/avaliar/descartar — sobrevivia na URL
 * e era aplicado junto. A contagem da aba vinha da faceta `faixa` sozinha e a
 * lista mostrava a INTERSECAO: a aba dizia 20 e a lista trazia 9; dizia 37 e
 * trazia 5.
 *
 * Medido na API: `faixa=avaliar` da 20, `recomendacao=avaliar&faixa=avaliar`
 * da 9. Os mesmos numeros que apareceram na tela.
 *
 * `recomendacao` continua existindo como faceta na rail lateral — ela e outra
 * pergunta, e a hierarquia edital > parecer > score segue valendo. O que ela
 * nao pode e sobreviver a uma troca de aba sem ninguem ver.
 */
const DIMENSOES_DA_ABA = {
  recomendacao: undefined,
  faixa: undefined,
  decisao: undefined,
  encontradas: undefined,
  etapa: undefined,
};

const ABAS: {
  // Nao e `string`: a chave TEM de ser um campo de `Abas`, senao uma aba nova
  // contaria zero para sempre e ninguem repararia. O `tsc` pegou isso assim
  // que a contagem passou a sair de `facetas.abas`.
  chave:
    | "passar"
    | "aguardando"
    | "avaliar"
    | "aprovadas"
    | "em_disputa"
    | "vencidas"
    | "perdidas"
    | "descartadas";
  rotulo: string;
  // `Partial<Filtros>` e nao `as const`: o objeto e passado a `alterar`, que
  // recebe filtros mutaveis, e `as const` os tornaria `readonly` — o `tsc`
  // pegou isso.
  filtro: Partial<Filtros>;
  ajuda: string;
  // A aba só é desenhada quando há alguém nela (ou quando é a que está
  // aberta). Ver "Aguardando leitura".
  soComItens?: true;
}[] = [
  // AS TRES DE TRABALHO EXCLUEM O QUE JA FOI DECIDIDO (`decisao: pendente`).
  // Sem isso, decidir nao esvaziava nada: a licitacao descartada continuava em
  // "Avaliar" para sempre, e nao havia como saber o que ainda falta olhar.
  //
  // A contagem TINHA de sair da faceta junto com o filtro. A faceta conta por
  // dimensao, uma de cada vez, e estas abas cruzam duas — enquanto elas liam
  // `facetas.faixa`, estreitar por decisao faria a aba dizer 37 e abrir 5, que
  // e literalmente o defeito que ja apareceu nesta tela. Por isso `abas`.
  // OS NOMES MUDARAM EM 2026-09-16, os criterios nao. "Passar" virou
  // "Descartadas" e o antigo "Descartadas" virou "Reprovadas", a pedido da
  // gestao: "descartada" e o que a REGRA tirou da frente (score de 50 a 69);
  // "reprovada" e o que uma PESSOA decidiu nao disputar. As chaves internas
  // (`passar`, `descartadas`) ficam — sao as da contagem do servidor, e
  // renomea-las nao muda nada para quem le a tela.
  // OS CRITERIOS SAO OS DO DOCUMENTO DE REGRAS DA NECTAR (2026-09-22,
  // docs/regras_de_negocio_nectar.md), e o servidor e quem os aplica
  // (`acervo.faixa_sql`). O texto aqui so os descreve.
  { chave: "passar", rotulo: "Descartadas", filtro: { faixa: ["passar"], decisao: ["pendente"] },
    ajuda: "Score de 50 a 69, ou 70 ou mais com contrato abaixo de R$ 250 mil, ainda sem decisão." },
  // AGUARDANDO LEITURA (D17, 2026-10-05; opção A do Pedro): a antessala de
  // Avaliar. Com o portão ligado no servidor (`acervo.portao_da_leitura`), a
  // oportunidade que teria score de Avaliar pela frase do objeto fica aqui,
  // SEM score, até o edital ser lido inteiro e cruzado com os atestados. Com
  // o portão desligado a contagem é sempre zero — e por isso a aba só aparece
  // quando há alguém nela: vazia para sempre, seria ruído.
  { chave: "aguardando", rotulo: "Aguardando leitura", soComItens: true,
    filtro: { faixa: ["aguardando"], decisao: ["pendente"] },
    ajuda: "Teria score para Avaliar, mas o edital ainda não foi lido inteiro e cruzado com os atestados. Sem score até a leitura sair; depois dela, vai para a aba da regra." },
  { chave: "avaliar", rotulo: "Avaliar", filtro: { faixa: ["avaliar"], decisao: ["pendente"] },
    ajuda: "Score 70 ou mais com contrato de pelo menos R$ 250 mil, ou sem valor informado, ainda sem decisão. É onde o trabalho do dia começa." },
  // O FIM DO FUNIL (D13, 2026-10-05). Estas quatro filtram pela ETAPA, que o
  // servidor deriva (`disputa.etapa_sql`), e não mais por `decisao=aprovada`:
  // a aprovada que entrou em disputa, venceu ou perdeu sai de Aprovadas para a
  // aba dela, e a aprovada que passou do prazo sem disputar vai para Perdidas.
  // NENHUMA das abas das decisões perde a licitação pelo prazo (03/11): as três
  // últimas e Reprovadas ficam fora do corte de prazo no servidor, e a vencida
  // aparece com a etiqueta "Prazo vencido".
  { chave: "aprovadas", rotulo: "Aprovadas", filtro: { etapa: ["aprovada", "preparacao"] },
    ajuda: "Uma pessoa aprovou, e a licitação ainda não entrou em disputa. As que já têm a documentação em preparação vêm marcadas." },
  { chave: "em_disputa", rotulo: "Em Disputa", filtro: { etapa: ["em_disputa"] },
    ajuda: "Alguém informou que a Nectar entrou na disputa. Falta informar o resultado: vencida ou perdida." },
  { chave: "vencidas", rotulo: "Vencidas", filtro: { etapa: ["vencida"] },
    ajuda: "A Nectar venceu a licitação." },
  { chave: "perdidas", rotulo: "Perdidas", filtro: { etapa: ["perdida"] },
    ajuda: "A Nectar perdeu a disputa — ou aprovou e o prazo passou sem a disputa ser informada." },
  // O PAR DA APROVADAS, e a razao de as abas acima poderem estreitar: sair da
  // fila nao pode ser sumir da tela.
  { chave: "descartadas", rotulo: "Reprovadas", filtro: { decisao: ["descartada"] },
    ajuda: "Uma pessoa decidiu não disputar. Continua aqui, mesmo com o prazo vencido: é o histórico que calibra o corte." },
];

// Fora de ABAS porque a Encontradas não é faixa nem decisão; constante para o
// `title` e a ajuda do toque dizerem a mesma frase.
const AJUDA_ENCONTRADAS =
  "O que a última varredura encontrou — a que está em andamento, ou a mais recente — e ainda não tem score. Ao ganhar score, cada licitação sai daqui para a aba da regra dela; abaixo de 50, deixa de aparecer.";

// Só abaixo de 980px o painel cobre a lista e os filtros. Na gaveta de
// 981-1180px eles seguem clicáveis, e cada filtro reescreve a entrada
// (replace) sem o `painel`: a pilha divergiria.
const SOBREPOSTO = "(max-width: 980px)";

const ORDENS = [
  // O `valor` continua "score" porque vaza para a querystring e é a ordem
  // padrão — renomear quebraria URL que alguém já salvou. O RÓTULO é que
  // estava errado: dizia "Aderência" desde antes de a etapa 4 existir, quando
  // a ordenação de fato usava o peso da aderência da triagem.
  { valor: "score", rotulo: "Maior score" },
  // A praça declarada, as capitais e as cidades grandes na frente, cada bloco
  // do maior score para o menor (o Pedro, 03/11). "Maior score" continua puro.
  { valor: "local", rotulo: "Prioridade geográfica" },
  { valor: "prazo", rotulo: "Prazo mais próximo" },
  { valor: "recente", rotulo: "Vistas por último" },
  { valor: "orgao", rotulo: "Órgão" },
] satisfies { valor: Filtros["ordem"]; rotulo: string }[];

/** A URL pede só o que uma pessoa já decidiu (Aprovadas, Reprovadas e as
 *  abas do fim do funil, que só existem depois de aprovar)? */
function soDecididas(p: URLSearchParams): boolean {
  if (p.getAll("etapa").length > 0) return true;
  const d = p.getAll("decisao");
  return d.length > 0 && d.every((v) => v !== "pendente");
}

/** O filtro inteiro vive na URL: recarregar, voltar e mandar o link funcionam. */
function lerFiltros(p: URLSearchParams): Filtros {
  const lista = (chave: string) => {
    const v = p.getAll(chave);
    return v.length ? v : undefined;
  };
  const vetadas = p.get("vetadas");
  return {
    recomendacao: lista("recomendacao"),
    // O PADRAO NAO E "tudo": e a fila. Sem `faixa` na URL, entram so
    // `avaliar` e `passar` — abaixo do piso a oportunidade nao aparece, que e
    // a regra pedida. Aplicar isto aqui, na leitura dos filtros, e o que faz
    // valer tambem para quem chega por link direto ou recarrega a pagina; se
    // morasse so no clique da aba, a primeira carga mostraria as 784.
    //
    // Score abaixo de 50 nao aparece nem pedindo: a Regra 1 do documento de
    // regras vale no servidor (`acervo._onde`) desde 2026-09-22, e
    // `faixa=descartar` volta vazia. O que era "nada fica inalcancavel" virou
    // o contrario, por regra: "nao devera ser exibida para analise".
    //
    // E AS ABAS DE DECISAO NAO LEVAM O PADRAO: Aprovadas e Reprovadas mostram
    // tudo o que foi decidido e que a Regra 1 deixa ver. Com o padrao, o
    // numero da aba (sem faixa) e a lista (com faixa) contavam coisas
    // diferentes — a Reprovadas dizia 2 e abria vazia.
    // A ABA "Encontradas" NAO aplica esse padrao (2026-09-18): ela mostra o
    // que o funil ainda nao julgou, e quem nao foi julgada nao tem faixa —
    // o padrao `avaliar, passar` a esvaziaria inteira. As julgadas saem de la
    // sozinhas, para a aba do score delas.
    //
    // `aguardando` ENTRA NO PADRAO (D17, 2026-10-05): o padrao existe para
    // deixar de fora o que esta abaixo do piso, e nao quem espera a leitura
    // do edital — sem ela aqui, a busca sem aba nao acharia a oportunidade
    // que aguarda. Sem o portao ligado ninguem tem essa faixa.
    faixa:
      p.get("encontradas") === "true" || soDecididas(p)
        ? lista("faixa")
        : (lista("faixa") ?? ["avaliar", "aguardando", "passar"]),
    encontradas: p.get("encontradas") === "true" ? true : undefined,
    uf: lista("uf"),
    nivel: lista("nivel"),
    edital: lista("edital"),
    termo: lista("termo"),
    categoria: lista("categoria"),
    aderencia: lista("aderencia"),
    decisao: lista("decisao"),
    etapa: lista("etapa"),
    // Ausente na URL = o padrão da tela, que é esconder as vetadas.
    vetadas: vetadas === null ? false : vetadas === "true" ? true : undefined,
    busca: p.get("busca") ?? undefined,
    ordem: (p.get("ordem") as Filtros["ordem"]) ?? "score",
  };
}

export function Oportunidades() {
  const [params, setParams] = useSearchParams();
  // Volta do histórico com o painel aberto (a tela dedicada e o voltar dela):
  // a entrada lembra qual estava aberta.
  const [selecionada, setSelecionada] = useState<string | null>(() =>
    window.matchMedia(SOBREPOSTO).matches ? (window.history.state?.painel ?? null) : null);
  const voltando = useRef(false);
  // Quem abriu o painel, para o foco voltar a ele ao fechar: o Fechar sai do
  // DOM, e o foco caía no <body> — quem navega por teclado recomeçava do topo.
  const origem = useRef<HTMLElement | null>(null);
  const filtros = useMemo(() => lerFiltros(params), [params]);
  const navega = useNavigate();
  function selecionar(id: string | null) {
    // NO CELULAR, DIRETO PARA A TELA DEDICADA (pedido do Pedro, 2026-10-02).
    // Ate aqui o toque abria o painel por cima da lista, e era preciso tocar
    // de novo para chegar a tela da licitacao — dois passos para o mesmo
    // destino. O voltar da tela dedicada traz de volta a lista, na mesma
    // posicao (POP fica com o navegador, ver `Moldura`). De 981px para cima
    // o painel continua: ali ele divide a tela com a lista.
    if (id && window.matchMedia(SOBREPOSTO).matches) {
      navega(`/licitacao/${id}`);
      return;
    }
    if (id) origem.current = document.activeElement as HTMLElement | null;
    setSelecionada(id);
  }
  useEffect(() => {
    if (selecionada) return;
    const alvo = origem.current;
    origem.current = null;
    if (alvo?.isConnected) alvo.focus({ preventScroll: true });
  }, [selecionada]);

  // No celular o painel cobre a tela, e o voltar do aparelho é o primeiro
  // gesto de quem quer sair dele; sem uma entrada no histórico, esse voltar
  // saía da tela inteira e perdia a posição na lista.
  useEffect(() => {
    if (!selecionada || !window.matchMedia(SOBREPOSTO).matches) return;
    const estado = window.history.state ?? {};
    if (estado.painel !== selecionada) {
      const novo = { ...estado, painel: selecionada };
      // Trocar de licitação com o painel aberto substitui a entrada: um
      // voltar tem de fechar o painel, e não percorrer cada cartão aberto.
      if (estado.painel) window.history.replaceState(novo, "");
      else window.history.pushState(novo, "");
    }
    const aoVoltar = () => {
      voltando.current = false;
      setSelecionada(null);
    };
    window.addEventListener("popstate", aoVoltar);
    return () => window.removeEventListener("popstate", aoVoltar);
  }, [selecionada]);

  function fechar() {
    const marca = window.history.state?.painel;
    if (window.matchMedia(SOBREPOSTO).matches && marca === selecionada) {
      // Uma volta só: o efeito sem dependências abaixo chama isto a cada
      // render até o popstate chegar, e duas voltas sairiam da tela.
      if (!voltando.current) {
        voltando.current = true;
        window.history.back();
      }
      return;
    }
    // A MARCA E DE OUTRA ABERTURA (girar o tablet entre 980 e 1180px com o
    // painel aberto deixava a entrada do historico de uma licitacao antiga):
    // voltar ali consumia o toque sem fechar nada, e `voltando` ficava preso em
    // true — dai em diante o Fechar parava de funcionar. Fecha direto e limpa.
    voltando.current = false;
    if (marca) window.history.replaceState({ ...window.history.state, painel: undefined }, "");
    setSelecionada(null);
  }

  // A GAVETA DE 981 A 1180px fecha com Esc, como toda sobreposicao: ela cobre
  // a barra de navegacao do lado direito enquanto esta aberta.
  useEffect(() => {
    if (!selecionada) return;
    const esc = (e: KeyboardEvent) => {
      if (e.key === "Escape" && window.matchMedia("(max-width: 1180px)").matches) fechar();
    };
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  });

  // A TELA ABRE EM "AVALIAR" (2026-09-16). Sem nenhum parametro na URL — o
  // clique no menu, o "voltar para as oportunidades" — ela mostrava Avaliar e
  // Descartadas misturadas, com nenhuma aba marcada, que era a antiga "Na
  // fila". `replace`, para o voltar do navegador nao cair de novo aqui vazio.
  useEffect(() => {
    if (params.toString() === "") {
      setParams({ faixa: "avaliar", decisao: "pendente" }, { replace: true });
    }
  }, [params, setParams]);

  // APROVADAS E REPROVADAS POR DIA (20/10): nas duas abas do que uma pessoa
  // decidiu, a lista vem pela data da decisão, a mais recente em cima, e
  // separada por dia. A ordem escolhida no seletor não vale ali — é o dia da
  // decisão que organiza — e o seletor dá lugar ao aviso.
  const etapas = [...(filtros.etapa ?? [])].sort().join(",");
  const soDecididas =
    !filtros.encontradas &&
    !(filtros.faixa ?? []).length &&
    ((etapas === "aprovada,preparacao" && !(filtros.decisao ?? []).length) ||
      (!etapas && (filtros.decisao ?? []).join() === "descartada"));
  const pedido = soDecididas ? { ...filtros, ordem: "decisao" as const } : filtros;

  const lista = useQuery({
    queryKey: ["licitacoes", pedido],
    queryFn: () => api.listar(pedido, 0, 1000),
    // Sem isto a lista pisca em branco a cada clique de faceta, e a página
    // pula porque a altura vai a zero e volta.
    placeholderData: keepPreviousData,
  });

  const facetas = useQuery({ queryKey: ["facetas"], queryFn: api.facetas });
  const itens = lista.data?.itens ?? [];

  // Uma conta so, vinda do servidor, com o MESMO recorte que a aba aplica.
  // Antes cada aba procurava seu numero na faceta da dimensao que ela filtrava
  // — e isso so funcionava enquanto nenhuma aba cruzasse duas dimensoes.
  const contarAba = (chave: keyof NonNullable<typeof facetas.data>["abas"]) =>
    facetas.data?.abas[chave] ?? 0;
  // Aba ativa = o filtro que ela aplica, quando há exatamente ele. Com nenhum
  // ou vários, nenhuma aba fica marcada — o estado da tela nunca mente.
  const abaAtiva = (() => {
    if (filtros.encontradas) return "encontradas";
    const fx = filtros.faixa ?? [];
    const dec = filtros.decisao ?? [];
    const so = (v: string) => dec.length === 1 && dec[0] === v;
    // AS ABAS DO FIM DO FUNIL filtram só pela etapa. Qualquer outra dimensão
    // junto não é aba nenhuma — o estado da tela nunca mente.
    const et = filtros.etapa ?? [];
    if (et.length) {
      if (fx.length || dec.length) return null;
      return (
        {
          "aprovada,preparacao": "aprovadas",
          em_disputa: "em_disputa",
          vencida: "vencidas",
          perdida: "perdidas",
        } as Record<string, string>
      )[[...et].sort().join(",")] ?? null;
    }
    if (so("pendente")) {
      if (fx.length === 1) return fx[0];
      return fx.length ? "fila" : null;
    }
    // Aprovadas e Reprovadas nao levam faixa (ver `lerFiltros`). Antes elas
    // recebiam o padrao de duas faixas e nunca apareciam marcadas.
    if (fx.length) return null;
    // `decisao=aprovada` (o link antigo da aba) ainda lista, mas não é mais a
    // aba Aprovadas: ele traz junto a aprovada que já entrou em disputa.
    if (so("descartada")) return "descartadas";
    return null;
  })();
  // "fila" e faixa solta vinda da URL não são aba: sem ajuda, e sem <p> vazio.
  const ajudaAtiva =
    abaAtiva === "encontradas"
      ? AJUDA_ENCONTRADAS
      : ABAS.find((a) => a.chave === abaAtiva)?.ajuda;


  function alterar(mudanca: Partial<Filtros>) {
    const p = new URLSearchParams(params);
    for (const [chave, valor] of Object.entries(mudanca)) {
      p.delete(chave);
      if (valor === undefined || valor === "") continue;
      if (Array.isArray(valor)) valor.forEach((v) => p.append(chave, String(v)));
      else p.set(chave, String(valor));
    }
    setParams(p, { replace: true });
  }

  // A seleção só cai se o item sumiu do resultado. Limpá-la junto com o
  // filtro criava duas atualizações de estado na mesma interação, e o
  // checkbox — que é controlado pela URL — piscava de volta para desmarcado
  // entre um render e outro. Além de ser melhor assim: trocar um filtro não
  // deve fechar o detalhe que você está lendo.
  //
  // Fecha pelo mesmo caminho do botão: decidir tira o item da aba, e a entrada
  // que o painel empilhou no celular tem de sair junto. `lista.data`: o painel
  // restaurado do histórico não fecha enquanto a lista ainda carrega.
  useEffect(() => {
    if (selecionada && lista.data && !itens.some((i) => i.id === selecionada)) {
      fechar();
    }
  });

  const total = lista.data?.total ?? 0;
  const vetadasNoAcervo =
    facetas.data?.veto.reduce((s, c) => s + c.total, 0) ?? 0;

  return (
    <div className={css.tela}>
      <Cabecalho
        titulo="Oportunidades"
        descricao={
          <>
            {lista.isPending
              ? "Lendo o acervo…"
              : `${total} ${total === 1 ? "licitação" : "licitações"}`}
            {/* O denominador e o universo que ESTA tela alcanca: o que o funil
                deixou passar, nao o acervo. Dizia "no acervo" mostrando 1.353 com
                2.427 no banco — os 1.074 cortados por prazo e situacao somem em
                silencio, e a tela afirmava que nunca existiram. Quem quer a conta
                do corte tem a tela Fila. */}
            {facetas.data && total !== facetas.data.total
              ? ` de ${facetas.data.total} no funil`
              : ""}
          </>
        }
        acoes={
          <>
            <Busca
              rotulo="Buscar nos resultados"
              placeholder="Buscar por órgão, objeto, município…"
              defaultValue={filtros.busca ?? ""}
              onChange={(e) => {
                const v = e.currentTarget.value;
                // Debounce curto: a busca é local ao servidor e responde em
                // milissegundos, mas disparar a cada tecla enche o histórico.
                clearTimeout((window as any).__buscaTimer);
                (window as any).__buscaTimer = setTimeout(
                  () => alterar({ busca: v || undefined }),
                  250,
                );
              }}
            />

            {soDecididas ? (
              // Nas abas do que uma pessoa decidiu a ordem é o dia da decisão:
              // o lugar do seletor, com o mesmo rótulo, diz qual é.
              <span className={css.ordem}>
                <span>Ordenar por</span>
                <b className={css.ordemFixa}>Data da decisão</b>
              </span>
            ) : (
              <Seletor
                rotulo="Ordenar por"
                valor={filtros.ordem ?? "score"}
                aoMudar={(v) => alterar({ ordem: v })}
                opcoes={ORDENS}
              />
            )}
          </>
        }
      />

      {/* O chip das vetadas é a peça que impede o filtro de virar censura:
          o número fica sempre à vista e volta com um clique. */}
      {vetadasNoAcervo > 0 && (
        <div className={css.avisoVeto}>
          {filtros.vetadas === false ? (
            <>
              <strong>{vetadasNoAcervo}</strong> licitações foram descartadas
              pelo objeto antes de baixar o edital.{" "}
              <button type="button" onClick={() => alterar({ vetadas: "true" as never })}>
                Ver o que foi filtrado
              </button>
            </>
          ) : filtros.vetadas === true ? (
            <>
              Mostrando <strong>só</strong> o que o filtro derrubou.{" "}
              <button type="button" onClick={() => alterar({ vetadas: undefined })}>
                Voltar ao normal
              </button>
            </>
          ) : (
            <>
              Mostrando vetadas e não vetadas juntas.{" "}
              <button type="button" onClick={() => alterar({ vetadas: "false" as never })}>
                Esconder as vetadas
              </button>
            </>
          )}
        </div>
      )}

      {/* NAO SE CHAMA MAIS "Todas", e o nome importa: ela deixou de mostrar
            tudo. Abaixo de `score.piso_passar` a oportunidade nao aparece
            aqui — sao 784 das 841 neste banco, e rolar por elas para achar as
            57 que valem era o trabalho que a aba existia para eliminar.

            "Todas" continuaria dizendo que mostra tudo, e seria mentira. O
            que sobrou e a FILA: o que ainda pode virar trabalho.

            Desde 2026-09-22 o score abaixo de 50 nao aparece em lugar
            nenhum da tela, nem pela faceta: e a Regra 1 do documento de
          regras, e o servidor a aplica (`acervo._onde`). */}
      {/* ENCONTRADAS NO LUGAR DE "NA FILA" (2026-09-16): o que a ultima
          varredura trouxe — a em curso, ou a mais recente —, com qualquer
          score. Recem-encontrada nao tem score ate o acompanhante triar, e
          nenhuma outra aba a mostraria. */}
      {/* NO CELULAR as abas são UMA linha que rola para os lados, e a ativa vem
          ao centro dela (`centraAtiva`). */}
      <Abas
        rotulo="Faixa de score"
        centraAtiva
        ativa={abaAtiva ?? null}
        aoMudar={(chave) =>
          alterar(
            chave === "encontradas"
              ? { ...DIMENSOES_DA_ABA, encontradas: true }
              : { ...DIMENSOES_DA_ABA, ...ABAS.find((a) => a.chave === chave)!.filtro },
          )
        }
        abas={[
          {
            valor: "encontradas",
            rotulo: "Encontradas",
            conta: contarAba("encontradas"),
            ajuda: AJUDA_ENCONTRADAS,
          },
          ...ABAS.filter(
            (a) => !a.soComItens || contarAba(a.chave) > 0 || abaAtiva === a.chave,
          ).map((a) => ({ valor: a.chave, rotulo: a.rotulo, conta: contarAba(a.chave), ajuda: a.ajuda })),
        ]}
      />
      {/* Só aparece no toque (o CSS a esconde onde há hover): ali o `title`
          não se descobre, e a regra de cada aba ficava inalcançável. */}
      {ajudaAtiva && <p className={css.ajudaAba}>{ajudaAtiva}</p>}

      <div className={css.grade}>
        <Facetas dados={facetas.data} filtros={filtros} aoMudar={alterar} />

        <ListaLicitacoes
          porDia={soDecididas}
          itens={itens}
          carregando={lista.isPending}
          selecionada={selecionada}
          aoSelecionar={selecionar}
        />

        <PainelDetalhe id={selecionada} aoFechar={fechar} />
      </div>
    </div>
  );
}
